import { errorLogService } from '../services/errorLogService';

// ============================================
// DETECÇÃO DE EXTENSÕES DE BROWSER
// ============================================

/**
 * Detecta extensões de browser ativas que são conhecidas por modificar o DOM
 * e causar o erro "removeChild" no React.
 *
 * Retorna array com os nomes das extensões detectadas via assinaturas DOM/globais.
 * Se nenhuma for identificada, retorna array vazio.
 */
export function detectActiveExtensions() {
  const detected = [];

  try {
    // Google Translate (injeta class na tag <html>)
    if (
      document.documentElement.classList.contains('translated-ltr') ||
      document.documentElement.classList.contains('translated-rtl')
    ) {
      detected.push('Google Translate');
    }

    // Microsoft Edge Translator (adiciona atributo _msttexthash no <title>)
    if (document.querySelector('title[_msttexthash]')) {
      detected.push('Microsoft Edge Translator');
    }

    // Grammarly (injeta custom element ou atributos data-gramm)
    if (
      document.querySelector('grammarly-extension') ||
      document.querySelector('grammarly-desktop-integration') ||
      document.querySelector('[data-gramm]')
    ) {
      detected.push('Grammarly');
    }

    // LastPass (injeta spans com id __lpform_ ou window.__lpobj__)
    if (
      window.__lpobj__ ||
      document.querySelector('span[id^="__lpform_"]') ||
      document.querySelector('[data-lastpass-icon-root]')
    ) {
      detected.push('LastPass');
    }

    // Honey / PayPal Honey (injeta #honey-container ou window.honey)
    if (
      window.honey ||
      document.getElementById('honey-container')
    ) {
      detected.push('Honey');
    }

    // Tradutores genéricos que envolvem TextNodes em <font> (TransOver, 3CX, etc.)
    // Heurística: presença de <font> filhos diretos de qualquer elemento do app
    const fontEls = document.querySelectorAll('#root font');
    if (fontEls.length > 0) {
      detected.push('Tradutor de página (extensão desconhecida)');
    }
  } catch (e) {
    // Falha silenciosa — a detecção nunca deve impedir o fluxo principal
  }

  return detected;
}

/**
 * Inicializa o sistema de logging de erros do frontend
 * Sobrescreve console.error e capture global errors
 */
export function initializeErrorLogging() {
  // ============================================
  // 1. MONKEY-PATCH Node.prototype.removeChild
  //    Captura detalhes do nó antes do crash para diagnóstico
  // ============================================
  if (typeof Node === 'function' && Node.prototype) {
    const originalRemoveChild = Node.prototype.removeChild;
    Node.prototype.removeChild = function (child) {
      if (child.parentNode !== this) {
        // Coleta detalhes do nó órfão para diagnóstico
        const nodeDetails = {
          tagName: child.tagName || null,
          nodeType: child.nodeType,
          nodeValue: child.nodeValue ? String(child.nodeValue).slice(0, 200) : null,
          id: child.id || null,
          className: (typeof child.className === 'string' ? child.className : null),
          dataset: child.dataset ? { ...child.dataset } : null,
          actualParentTag: child.parentNode ? child.parentNode.tagName : null,
          expectedParentTag: this.tagName || null,
        };

        const extensions = detectActiveExtensions();

        console.error(
          '[DOM Patch] removeChild: nó não é filho do pai esperado. Provável interferência de extensão de browser.',
          '\nDetalhes do nó:', nodeDetails,
          '\nExtensões detectadas:', extensions.length > 0 ? extensions : ['nenhuma identificada'],
        );

        // Re-lança o erro original — o ErrorBoundary irá capturá-lo
        return originalRemoveChild.apply(this, arguments);
      }
      return originalRemoveChild.apply(this, arguments);
    };
  }

  // ============================================
  // 2. INTERCEPTAR console.error()
  //    Filtra erros do ErrorBoundary (enviados diretamente via componentDidCatch)
  //    para evitar double-logging no backend.
  // ============================================
  const originalError = console.error;
  console.error = function (...args) {
    // Chama original primeiro (mantém comportamento esperado no console)
    originalError.apply(console, args);

    const message = args.map(a => a instanceof Error ? a.message : String(a)).join(' ');

    // Não envia ao backend erros já tratados pelo ErrorBoundary
    // (identificados pelo prefixo [ERROR_BOUNDARY]) — componentDidCatch os envia com mais contexto
    if (message.includes('[ERROR_BOUNDARY]')) return;

    if (args.length > 0) {
      const errorObj = args.find(a => a instanceof Error);
      const errorToLog = errorObj
        ? Object.assign(new Error(message), { stack: errorObj.stack })
        : message;

      errorLogService.logError(errorToLog).catch(() => {});
    }
  };

  // ============================================
  // 3. CAPTURAR ERROS GLOBAIS NÃO-TRATADOS
  // ============================================
  window.addEventListener('error', (event) => {
    errorLogService.logError(event.error || event.message).catch(() => {});
  });

  // ============================================
  // 4. CAPTURAR PROMISE REJECTIONS NÃO-TRATADAS
  // ============================================
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error
      ? event.reason
      : new Error(String(event.reason));
    errorLogService.logError(error).catch(() => {});
  });

  console.log('[ErrorLogging] Sistema inicializado com sucesso');
}
